import { setTag, setUser } from "@sentry/react"
import { useEffect } from "react"

import type { Org } from "@/api/orgs"
import type { User } from "@/api/users"
import { logEvent } from "@/util/telemetry"
import type { JsonObject } from "@/util/types"
import { viewManager } from "@/util/viewManager"

// Third-party analytics SDKs (FullStory, LogRocket, PostHog) attach themselves
// to `window` at runtime. We access them through a narrow structural type
// rather than `any` so property accesses remain type-checked.
interface AnalyticsClient {
  identify?: (id: string, traits?: Record<string, unknown>) => void
}

interface AnalyticsWindow {
  fullstory?: AnalyticsClient
  logRocket?: AnalyticsClient
  posthog?: AnalyticsClient
}

function getAnalyticsWindow(): AnalyticsWindow {
  return window as unknown as AnalyticsWindow
}

export function identify(org: Org, user: User, metadata?: Record<string, unknown>) {
  if (process.env.NODE_ENV === "development") {
    logEvent("info", "analytics: identify", {
      context: { org, user },
      extra: { metadata },
    })
    return
  }

  const { name, email, orgId } = user
  const { salesforceId } = org
  const parentOrg = org.parentOrg ?? undefined
  const baseOrg = parentOrg ?? org

  const { fullstory, logRocket, posthog } = getAnalyticsWindow()

  fullstory?.identify?.(email, {
    displayName: name,
    email,
    orgId,
    orgName: org.name,
    parentOrgId: parentOrg?.id,
    parentOrgName: parentOrg?.name,
    baseOrgId: baseOrg.id,
    baseOrgName: baseOrg.name,
    salesforceId,
    ...metadata,
  })

  logRocket?.identify?.(email, {
    name,
    email,
    org_id: orgId,
    org_name: org.name,
    parent_org_id: parentOrg?.id,
    parent_org_name: parentOrg?.name,
    base_org_id: baseOrg.id,
    base_org_name: baseOrg.name,
    salesforce_id: salesforceId,
    ...metadata,
  })

  posthog?.identify?.(email, {
    name,
    email,
    orgId,
    orgName: org.name,
    parentOrgId: parentOrg?.id,
    parentOrgName: parentOrg?.name,
    baseOrgId: baseOrg.id,
    baseOrgName: baseOrg.name,
    salesforceId,
    ...metadata,
  })

  setUser?.({ email })
  setTag("org", baseOrg.subdomain)
  setTag("user", email)

  const embedId = viewManager.get("org")?.embed?.id ?? org.embed?.id
  setTag("embedId", embedId)
}

export function page(params?: string | JsonObject) {
  logEvent("info", "analytics: page event", { extra: { params } })
}

export function track(event: string, properties?: JsonObject) {
  logEvent("info", "analytics: track event", { extra: { event, properties } })
}

export function usePageTracking(pageName: string) {
  useEffect(() => page(pageName), [pageName])
}
